import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";

// Partner-level communication defaults. Despite living visually inside the
// per-property Communication defaults card, this endpoint is scoped to the
// current partner (no property id in the path) — it is served by boompay-api
// (NOT comm-service), so we use the portal `instance` client.
const COMMUNICATION_DEFAULTS_EMAIL = "/portal/v1/communication_defaults/email";

const COMMUNICATION_DEFAULT_EMAIL_KEY = [
  "COMMUNICATION_DEFAULT_EMAIL"
] as const;

interface CommunicationDefaultEmail {
  communication_default_email: string | null;
  resolved_communication_default_email: string | null;
  available_emails: string[];
}

const communicationDefaultsApi = {
  getEmail: async (): Promise<CommunicationDefaultEmail> => {
    return (await instance.get(COMMUNICATION_DEFAULTS_EMAIL)).data;
  },

  setEmail: async (email: string): Promise<CommunicationDefaultEmail> => {
    return (
      await instance.patch(COMMUNICATION_DEFAULTS_EMAIL, {
        communication_default_email: email
      })
    ).data;
  }
};

export const useCommunicationDefaultEmail = (enabled: boolean = true) =>
  useQuery({
    queryKey: COMMUNICATION_DEFAULT_EMAIL_KEY,
    queryFn: communicationDefaultsApi.getEmail,
    enabled,
    // The endpoint 403s when the partner lacks the communication feature —
    // don't retry, let the consumer degrade gracefully on error.
    retry: false
  });

export const useSetCommunicationDefaultEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["COMMUNICATION_DEFAULT_EMAIL", "UPDATE"],
    mutationFn: (email: string) => communicationDefaultsApi.setEmail(email),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: COMMUNICATION_DEFAULT_EMAIL_KEY
      });
    }
  });
};
